import type { ReactNode } from 'react';

import type { ChangeEntry } from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/Button';
import { DetailCard, DetailTabs } from '@/shared/ui/DetailLayout';
import { HistoryTable } from '@/shared/ui/HistoryTab';

import { rotuloDaAcao } from '../rotulos';

/** Valor so de leitura, com o rotulo do OnPoint. Vazio aparece como "nao informado". */
export function Info({
  label,
  value,
  hint,
  nota,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
  /** Texto pequeno abaixo do valor, como a origem do dado. */
  nota?: string | undefined;
}) {
  const vazio = value === null || value === undefined || value === '';
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {label}
      </dt>
      <dd
        className={cn('text-sm', vazio ? 'text-ink-400' : 'text-ink-900')}
        title={hint}
      >
        {vazio ? 'nao informado' : value}
      </dd>
      {nota && !vazio ? <dd className="text-2xs text-ink-500">{nota}</dd> : null}
    </div>
  );
}

export function InfoGrid({ children }: { children: ReactNode }) {
  return <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>;
}

/** Bloco com titulo dentro de uma secao. */
export function Bloco({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          {titulo}
        </h3>
        {acao}
      </div>
      {children}
    </section>
  );
}

/**
 * Cartao de secao do detalhe, com o estado de carregamento e de erro. E o
 * `DetailCard` comum (P-FUN-2b), com os nomes que as secoes do tomador ja usam.
 */
export function Secao({
  titulo,
  descricao,
  acao,
  carregando,
  erro,
  children,
}: {
  titulo: string;
  descricao?: string | undefined;
  acao?: ReactNode;
  carregando?: boolean | undefined;
  erro?: Error | null | undefined;
  children: ReactNode;
}) {
  return (
    <DetailCard
      title={titulo}
      description={descricao}
      action={acao}
      loading={carregando}
      error={erro}
    >
      {children}
    </DetailCard>
  );
}

/** Abas internas de uma secao, como as de "Cadastro Completo" (`DetailTabs` comum). */
export function Abas<T extends string>({
  abas,
  ativa,
  onChange,
  rotulo,
}: {
  abas: { chave: T; titulo: string }[];
  ativa: T;
  onChange: (chave: T) => void;
  rotulo: string;
}) {
  return (
    <DetailTabs
      tabs={abas.map((aba) => ({ key: aba.chave, title: aba.titulo }))}
      active={ativa}
      onChange={onChange}
      label={rotulo}
    />
  );
}

/** Acao presente e indisponivel, com o motivo a vista. */
export function AcaoIndisponivel({
  rotulo,
  motivo,
}: {
  rotulo: string;
  motivo: string;
}) {
  return (
    <span className="inline-flex flex-col gap-1">
      <Button
        size="sm"
        variant="secondary"
        disabled
        title={motivo}
        aria-describedby={undefined}
      >
        {rotulo}
      </Button>
      <span className="max-w-xs text-2xs text-ink-500">{motivo}</span>
    </span>
  );
}

/**
 * Tabela de historico com "Visualizar alteracoes", sobre a `HistoryTable` comum
 * (P-FUN-2b), com o nome das acoes do tomador.
 */
export function TabelaDeHistorico({
  linhas,
  vazio,
}: {
  linhas: ChangeEntry[];
  vazio: string;
}) {
  return <HistoryTable rows={linhas} emptyText={vazio} actionLabel={rotuloDaAcao} />;
}
